import { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { fetchGuestMe, fetchGuestSummary } from '../../api/guestApi';
import useCustomerAuth from '../../hooks/useCustomerAuth';
import GuestSidebar from './GuestSidebar';
import { useToast } from '../ToastContext';
import { getLocalItem, getSessionItem } from '../../utils/safeStorage';
import useIsMobile from '../../../hooks/useIsMobile';
import MobileBottomNav from '../../../mobile/components/MobileBottomNav';

const publicNavItems = [
  { label: 'Home', path: '/', icon: 'bi-house-door' },
  { label: 'Villas & Homestays', path: '/listings', icon: 'bi-house-heart' },
  { label: 'Cabs', path: '/cabs', icon: 'bi-car-front' },
  { label: 'Holiday Packages', path: '/packages', icon: 'bi-umbrella' },
  { label: 'Become a Host', path: '/become-a-host', icon: 'bi-person-plus' },
];

const routeTitles = [
  { path: '/guest/dashboard', title: 'Dashboard' },
  { path: '/guest/bookings', title: 'My Bookings' },
  { path: '/guest/upcoming', title: 'Upcoming Stays' },
  { path: '/guest/payments', title: 'Payments' },
  { path: '/guest/invoices', title: 'Invoices' },
  { path: '/guest/messages', title: 'Messages' },
  { path: '/guest/wishlist', title: 'Wishlist' },
  { path: '/guest/support', title: 'Support' },
  { path: '/guest/profile', title: 'Profile Settings' },
];

const GuestDashboardLayout = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const toast = useToast();
  const customerAuth = useCustomerAuth();
  const [profile, setProfile] = useState(null);
  const [summary, setSummary] = useState({
    totalBookings: 0,
    upcomingStays: 0,
    pendingPayments: 0,
    unreadMessages: 0,
    openTickets: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useIsMobile();

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [me, summaryRes] = await Promise.all([fetchGuestMe(), fetchGuestSummary()]);
      setProfile(me);
      setSummary(summaryRes);
    } catch (err) {
      setError(err.message || 'Unable to load guest dashboard.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = customerAuth.accessToken || getSessionItem('customerAccessToken') || getLocalItem('customerAccessToken');
    if (!token) {
      setError('Please log in to access the guest dashboard.');
      setLoading(false);
      return;
    }
    loadData();
  }, [customerAuth.accessToken, loadData]);

  const handleLogout = async () => {
    await customerAuth.logout();
    toast.success('Logged out');
    navigate('/customer/login', { replace: true });
  };

  const title = useMemo(() => {
    const match = routeTitles.find((r) => pathname.startsWith(r.path));
    return match?.title || 'Guest';
  }, [pathname]);

  return (
    <div className="hv-guest-workspace min-h-screen bg-slate-50">
      <GuestSidebar
        profile={profile}
        summary={summary}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((v) => !v)}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
        onLogout={handleLogout}
      />

      <main className={`transition-all duration-200 ${collapsed ? 'lg:ml-20' : 'lg:ml-72'}`}>
        <div className="px-4 pb-10 pt-6 sm:px-6 lg:px-10">
          <div className="mb-5 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <div className="flex flex-wrap items-center gap-2">
              {publicNavItems.map((item) => (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => navigate(item.path)}
                  className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-emerald-50 hover:text-emerald-700"
                >
                  <i className={`bi ${item.icon}`} />
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Guest Workspace</p>
              <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>
            </div>
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:border-emerald-200 hover:text-emerald-700 lg:hidden"
            >
              <i className="bi bi-list" />
              Menu
            </button>
          </div>

          {error && (
            <div className="mb-5 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          )}

          <div className="rounded-3xl border border-slate-200 bg-white/70 p-6 shadow-sm backdrop-blur">
            {loading ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-28 animate-pulse rounded-2xl bg-slate-100" />
                ))}
              </div>
            ) : (
              <Outlet
                context={{
                  profile,
                  summary,
                  refreshProfile: loadData,
                  setProfile,
                }}
              />
            )}
          </div>
        </div>
      </main>
      {isMobile ? <MobileBottomNav /> : null}
    </div>
  );
};

export default GuestDashboardLayout;
